import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";

import AdminOverview from "./AdminOverview";
import AdminMessages from "./AdminMessages";
import AdminUsers from "./AdminUsers";
import CanonicalItemManager from "./CanonicalItemManager";
import CanonicalImageManager from "./CanonicalImageManager";
import ItemReviewManager from "./ItemReviewManager";
import VariantReviewManager from "./VariantReviewManager";
import ReleaseManager from "./ReleaseManager";
import AdminCatalogGuide from "./AdminCatalogGuide";
import AnalyticsDashboard from "./AnalyticsDashboard";
import BatchImportManager from "./BatchImportManager";
import InventoryManager from "./InventoryManager";
import CommerceManager from "./CommerceManager";
import PaymentProviderManager from "./PaymentProviderManager";
import SellerApplicationsManager from "./SellerApplicationsManager";
import HobbliVisionLauncher from "../hobblivision/HobbliVisionLauncher";
import { HOBBLI_SUPPORT_ID } from "./adminConfig";

import "./AdminDashboard.css";

const SECTION_META = {
  dashboard: {
    title: "Dashboard",
    subtitle: "A quick view of Hobbli activity and the work that needs attention.",
  },
  canonicalImages: { title: "Canonical Images", subtitle: "Upload, pair, review, and match front/back card scans." },
  catalog: {
    title: "Canonical Items",
    subtitle: "Search, create, inspect, and edit the shared Hobbli catalog.",
  },
  itemReview: {
    title: "Item Review",
    subtitle: "Review user-owned items that are not yet linked to canonical catalog records.",
  },
  releaseManager: {
    title: "Release Manager",
    subtitle: "Manage catalog families, releases, subsets, card groups, and variant applicability.",
  },
  catalogGuide: {
    title: "Catalog Guide",
    subtitle: "Quick admin reference for families, releases, canonical items, variants, and manual catalog maintenance.",
  },

  variantReview: {
    title: "Variant Review",
    subtitle: "Review user-suggested variants and add approved suggestions to the canonical variants table.",
  },
  imports: {
    title: "Batch Imports",
    subtitle: "Bulk add or enrich canonical catalog records from Excel files.",
  },
  messages: {
    title: "Messages",
    subtitle: "Read and manage support messages sent to Hobbli.",
  },
  users: {
    title: "Users",
    subtitle: "Inspect Hobbli accounts and collection activity.",
  },
  analytics: {
    title: "Analytics",
    subtitle: "Explore catalog, user, and platform trends with useful drilldowns.",
  },
  hobblivision: {
    title: "HobbliVision",
    subtitle: "Launch and test the current HobbliVision recognition workflow.",
  },
  commerce: {
    title: "Commerce Controls",
    subtitle: "Manage seller access, plans, fees, checkout policy, enforcement, and payment readiness.",
  },
  sellerApplications: {
    title: "Seller Applications",
    subtitle: "Review users who want to open a shop and enable seller access after approval.",
  },
  paymentProviders: {
    title: "Payment Providers",
    subtitle: "Track marketplace payment providers, environments, approval state, and seller connection readiness.",
  },
  products: {
    title: "Store Products",
    subtitle: "Manage Hobbli merchandise, inventory, images, and availability.",
  },
};

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [{ id: "dashboard", label: "Dashboard", icon: "⌂" }],
  },
  {
    label: "Catalog",
    items: [
      { id: "catalog", label: "Canonical Items", icon: "◇" },
      { id: "canonicalImages", label: "Canonical Images", icon: "▧" },
      { id: "releaseManager", label: "Release Manager", icon: "▤" },
      { id: "catalogGuide", label: "Catalog Guide", icon: "?" },
      { id: "itemReview", label: "Item Review", icon: "✓", badge: "items" },
      { id: "variantReview", label: "Variant Review", icon: "◈", badge: "variants" },
      { id: "imports", label: "Batch Imports", icon: "⇧" },
    ],
  },
  {
    label: "Community",
    items: [
      { id: "messages", label: "Messages", icon: "✉", badge: "messages" },
      { id: "users", label: "Users", icon: "◎" },
    ],
  },
  {
    label: "System",
    items: [
      { id: "analytics", label: "Analytics", icon: "▥" },
      { id: "hobblivision", label: "HobbliVision", icon: "✨" },
    ],
  },
  {
    label: "Commerce",
    items: [
      { id: "sellerApplications", label: "Seller Applications", icon: "✓" },
      { id: "commerce", label: "Commerce Controls", icon: "¤" },
      { id: "paymentProviders", label: "Payment Providers", icon: "$" },
      { id: "products", label: "Store Products", icon: "▣" },
    ],
  },
];

const ADMIN_SECTION_STORAGE_KEY = "hobbli-admin-active-section";

export default function AdminDashboard({ currentUser, contributorOnly = false }) {
  const [activeSection, setActiveSection] = useState(() => {
    const savedSection = sessionStorage.getItem(ADMIN_SECTION_STORAGE_KEY);
    return contributorOnly ? "canonicalImages" : (savedSection && SECTION_META[savedSection] ? savedSection : "dashboard");
  });
  const [itemReviewTargetId, setItemReviewTargetId] = useState(null);
  const [navCounts, setNavCounts] = useState({
    unreadMessages: 0,
    pendingItemReviews: 0,
    pendingVariantReviews: 0,
  });
  const [navLoading, setNavLoading] = useState(false);

  const fetchNavigationCounts = useCallback(async () => {
    setNavLoading(true);

    const { data, error } = await supabase.rpc("admin_navigation_counts", { p_support_id: HOBBLI_SUPPORT_ID });

    if (error) {
      console.warn("Unable to fetch admin navigation counts:", error);
      setNavLoading(false);
      return;
    }

    const counts = Array.isArray(data) ? data[0] : data;

    setNavCounts((prev) => ({
      unreadMessages: Number(counts?.unread_messages ?? prev.unreadMessages ?? 0),
      pendingItemReviews: Number(counts?.pending_item_reviews ?? prev.pendingItemReviews ?? 0),
      pendingVariantReviews: Number(counts?.pending_variant_reviews ?? prev.pendingVariantReviews ?? 0),
    }));

    setNavLoading(false);
  }, []);

  useEffect(() => {
    if (!contributorOnly) fetchNavigationCounts();
  }, [fetchNavigationCounts, contributorOnly]);

  useEffect(() => {
    sessionStorage.setItem(ADMIN_SECTION_STORAGE_KEY, activeSection);
  }, [activeSection]);

  const handleUnreadCountChange = useCallback((count) => {
    setNavCounts((prev) =>
      prev.unreadMessages === count ? prev : { ...prev, unreadMessages: count }
    );
  }, []);

  const handleItemReviewCountChange = useCallback((count) => {
    setNavCounts((prev) =>
      prev.pendingItemReviews === count ? prev : { ...prev, pendingItemReviews: count }
    );
  }, []);

  const handleVariantReviewCountChange = useCallback((count) => {
    setNavCounts((prev) =>
      prev.pendingVariantReviews === count ? prev : { ...prev, pendingVariantReviews: count }
    );
  }, []);

  const handleResolveVariantCard = useCallback((userItemId) => {
    if (userItemId === null || userItemId === undefined || userItemId === "") return;
    setItemReviewTargetId(userItemId);
    setActiveSection("itemReview");
  }, []);

  const effectiveSection = contributorOnly ? "canonicalImages" : activeSection;
  const visibleNavGroups = contributorOnly ? NAV_GROUPS.map(group => ({...group, items: group.items.filter(item => item.id === "canonicalImages")})).filter(group => group.items.length) : NAV_GROUPS;
  const currentMeta = SECTION_META[effectiveSection] || SECTION_META.dashboard;

  const badgeValueFor = useCallback(
    (type) => {
      if (type === "messages") return navCounts.unreadMessages;
      if (type === "items") return navCounts.pendingItemReviews;
      if (type === "variants") return navCounts.pendingVariantReviews;
      return 0;
    },
    [navCounts]
  );

  const renderedSection = useMemo(() => {
    switch (effectiveSection) {
      case "canonicalImages":
        return <CanonicalImageManager currentUser={currentUser} />;

      case "catalog":
        return <CanonicalItemManager currentUser={currentUser} />;

      case "releaseManager":
        return <ReleaseManager currentUser={currentUser} />;

      case "catalogGuide":
        return <AdminCatalogGuide />;

      case "itemReview":
        return (
          <ItemReviewManager
            currentUser={currentUser}
            onPendingCountChange={handleItemReviewCountChange}
            focusUserItemId={itemReviewTargetId}
            onFocusUserItemHandled={() => setItemReviewTargetId(null)}
          />
        );

      case "variantReview":
        return (
          <VariantReviewManager
            currentUser={currentUser}
            onPendingCountChange={handleVariantReviewCountChange}
            onResolveCard={handleResolveVariantCard}
          />
        );

      case "imports":
        return <BatchImportManager currentUser={currentUser} />;

      case "messages":
        return (
          <AdminMessages
            currentUser={currentUser}
            onUnreadCountChange={handleUnreadCountChange}
          />
        );

      case "users":
        return <AdminUsers currentUser={currentUser} />;

      case "analytics":
        return <AnalyticsDashboard />;

      case "hobblivision":
        return (
          <div className="hadmin-panel hadmin-hobblivision-panel">
            <div className="hadmin-section-heading">
              <div>
                <h2>HobbliVision Development Console</h2>
                <p>Run the current recognition flow without leaving the admin console.</p>
              </div>
            </div>

            <HobbliVisionLauncher
              currentUser={currentUser}
              user={currentUser}
              buttonText="Launch HobbliVision"
              onItemConfirmed={(item) => {
                console.log("Admin HobbliVision confirmed item:", item);
              }}
            />
          </div>
        );

      case "sellerApplications":
        return <SellerApplicationsManager currentUser={currentUser} />;

      case "commerce":
        return <CommerceManager currentUser={currentUser} />;

      case "paymentProviders":
        return <PaymentProviderManager currentUser={currentUser} />;

      case "products":
        return <InventoryManager currentUser={currentUser} />;

      case "dashboard":
      default:
        return (
          <AdminOverview
            currentUser={currentUser}
            onNavigate={setActiveSection}
            onCountsRefresh={fetchNavigationCounts}
          />
        );
    }
  }, [
    effectiveSection,
    currentUser,
    fetchNavigationCounts,
    handleItemReviewCountChange,
    handleResolveVariantCard,
    handleUnreadCountChange,
    handleVariantReviewCountChange,
    itemReviewTargetId,
  ]);

  return (
    <div className="hadmin-layout">
      <aside className="hadmin-sidebar">
        <div className="hadmin-sidebar-scroll">
          <div className="hadmin-brand">
            <div className="hadmin-brand-mark">H</div>
            <div>
              <h2>{contributorOnly ? "Hobbli Contributor" : "Hobbli Admin"}</h2>
              <span>Management Console</span>
            </div>
          </div>

          <nav className="hadmin-nav" aria-label="Admin navigation">
            {visibleNavGroups.map((group) => (
              <div className="hadmin-nav-group" key={group.label}>
                <div className="hadmin-nav-group-title">{group.label}</div>

                {group.items.map((item) => {
                  const badgeValue = item.badge ? badgeValueFor(item.badge) : 0;

                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`hadmin-nav-btn ${effectiveSection === item.id ? "active" : ""}`}
                      onClick={() => setActiveSection(item.id)}
                    >
                      <span className="hadmin-nav-icon" aria-hidden="true">{item.icon}</span>
                      <span>{item.label}</span>
                      {badgeValue > 0 && (
                        <span className="hadmin-badge">{badgeValue > 99 ? "99+" : badgeValue}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        </div>

        <div className="hadmin-sidebar-footer">
          <div className="hadmin-user-card">
            <span className="hadmin-user-label">Signed in as</span>
            <strong>{currentUser?.email || currentUser?.username || currentUser?.id || "Admin"}</strong>
          </div>
        </div>
      </aside>

      <main className="hadmin-main">
        <header className="hadmin-main-header">
          <div>
            <h1>{currentMeta.title}</h1>
            <p>{currentMeta.subtitle}</p>
          </div>
          {!contributorOnly && navLoading && <span className="hadmin-muted">Refreshing counts…</span>}
        </header>

        <div className="hadmin-content">{renderedSection}</div>
      </main>
    </div>
  );
}
